import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Youtube } from 'lucide-react';
import { BRAND } from '../mock';
import { Button } from './ui/button';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/teachings', label: 'Teachings' },
  { to: '/audio', label: 'Audio' },
  { to: '/dosage', label: 'Dosage' },
  { to: '/prayer', label: 'Prayer' },
  { to: '/events', label: 'Events' },
  { to: '/archive', label: 'Archive' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-[0_4px_20px_-8px_rgba(15,34,49,0.12)]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <img src={BRAND.logo} alt="Dispensers of Grace" className="h-10 md:h-11 w-auto object-contain" />
            <span className={`hidden xl:inline font-serif-display text-base leading-tight ${scrolled ? 'text-ink' : 'text-white'} transition-colors`}>
              Dispensers of Grace<br />
              <span className="text-[10px] tracking-[0.22em] uppercase text-gold-brand font-sans">Ministry</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `relative px-2.5 py-2 text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors ${
                    isActive
                      ? (scrolled ? 'text-teal-deep' : 'text-white')
                      : (scrolled ? 'text-ink/75 hover:text-teal-deep' : 'text-white/85 hover:text-white')
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span className={`pointer-events-none absolute left-2.5 right-2.5 -bottom-0.5 h-[2px] bg-gold-brand transition-transform origin-left ${isActive ? 'scale-x-100' : 'scale-x-0'}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <a href={BRAND.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className={`p-2 rounded-full transition-colors ${scrolled ? 'text-ink/60 hover:text-[#FF0000] hover:bg-black/5' : 'text-white/85 hover:text-[#FF0000] hover:bg-white/10'}`}>
              <Youtube className="h-5 w-5" />
            </a>
            <Link to="/prayer">
              <Button className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold shadow-sm h-9 px-3 text-sm">
                Pray <ChevronRight className="ml-0.5 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 rounded-md ${scrolled ? 'text-ink hover:bg-black/5' : 'text-white hover:bg-white/10'}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-white border-t border-black/5 ${
          open ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `px-3 py-3 rounded-md text-base font-medium ${
                  isActive ? 'bg-cream text-teal-deep' : 'text-ink/80 hover:bg-cream'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link to="/prayer" className="mt-2">
            <Button className="w-full bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold">
              Join in Prayer
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}
